/**
 * Page publique — Landing des retraites spirituelles
 * URL : /retraites
 *
 * SEO : H1 unique, meta description, JSON-LD Event pour chaque retraite listée.
 * Optimisée conversion : accroche émotionnelle, bénéfices, cards prochaines dates.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Leaf, HeartPulse, Compass, Calendar, MapPin, ArrowRight,
  Utensils, BedDouble, ChevronDown, CreditCard, Store, Quote, Star,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.bilan2vie.fr";

const BENEFITS = [
  { icon: Compass, title: "Te réaligner", desc: "Reprendre ton souffle et retrouver ta boussole intérieure quand la vie va trop vite." },
  { icon: HeartPulse, title: "Recharger tes énergies", desc: "Nettoyer, harmoniser et laisser ton corps se reposer profondément." },
  { icon: Sparkles, title: "Mettre du sens", desc: "Éclairer ce que tu traverses avec des outils spirituels ancrés et concrets." },
  { icon: Leaf, title: "Repartir outillé·e", desc: "Des rituels et pratiques simples à intégrer chez toi, dès le retour." },
];

// Avis authentiques des participant·es aux précédentes éditions du séjour.
// Utilisés pour : (1) section témoignages visuelle sur la page pilier
// (2) JSON-LD Review + AggregateRating → étoiles ⭐ dans les résultats Google.
const TESTIMONIALS = [
  { name: "Eugénie", text: "Ce week-end a été incroyable, autant au niveau de l'organisation qui était parfaite, des intervenants qui étaient exceptionnels, du groupe qui était puissant et bienveillant. Une bulle de bien-être qui a amorcé de grands changements en moi." },
  { name: "Aurelie", text: "J'ai eu la chance de rencontrer Guillaume lors de sa toute première escapade spirituelle. Pour une première, tout était absolument réussi : l'organisation, le lieu ainsi que les intervenants étaient justes et parfaitement alignés. Ces deux jours ont été une véritable parenthèse hors du temps… profondément reposants, mais surtout très riches intérieurement. Cette expérience m'a énormément apporté et m'a permis de trouver des réponses à de nombreuses questions que je me posais depuis un moment." },
  { name: "Anne-Laure", text: "Merci encore pour ce magnifique weekend qui était vraiment très bien organisé, riche en partages de connaissances et en moment de convivialité, ainsi que ces « castings » de participantes et d'intervenantes qui n'auraient pas pu être mieux ! Bravo à toi d'avoir réalisé cela !" },
  { name: "Vanessa", text: "Un week-end, hors du temps ! L'organisation au top, le site très agréable. Des intervenants passionnants et passionnés ! Guillaume a fait un travail extraordinaire pour l'organisation de ce week-end, et une bienveillance à tout niveau. Hâte de faire le prochain." },
  { name: "Fabienne", text: "J'ai adoré participer en tant qu'intervenante et participante, merci pour tout." },
  { name: "Virginie", text: "Merci à Guillaume, aux participantes, aux intervenants et aux hôtes pour ce week-end. Cet aparté qui m'a permise de me ressourcer." },
  { name: "Pauline", text: "Gratitude Guillaume de t'être lancé, c'est inspirant et ça donne envie d'en faire d'autres." },
  { name: "Ellena", text: "Merci pour ce beau week-end." },
  { name: "Laetitia", text: "Gratitude 😊" },
];

const FAQ = [
  {
    q: "À qui s'adresse cette retraite ?",
    a: "À toute personne qui ressent le besoin de se reconnecter à elle-même, de mettre du sens sur ce qu'elle traverse et de s'offrir une vraie parenthèse de ressourcement. Pas besoin de « niveau » particulier : débutant·e ou déjà familier·e des pratiques énergétiques, tu es le·la bienvenu·e.",
  },
  {
    q: "Puis-je venir seul·e ?",
    a: "Oui, bien sûr. La plupart des participant·es viennent seul·es. Le groupe crée rapidement une atmosphère de confiance et de partage.",
  },
  {
    q: "Que dois-je apporter ?",
    a: "Des vêtements confortables, un carnet et un stylo, ta gourde, tes oracles, ton pendule, éventuellement un plaid ou un coussin de méditation si tu en as l'habitude. Un récapitulatif complet te sera envoyé par email avant la retraite.",
  },
  {
    q: "Et si j'ai un empêchement ?",
    a: "L'acompte de 30 % reste acquis à Bilan 2 Vie dès que la place est bloquée, quelle que soit la date d'annulation. Entre J-45 et J-30, le solde te sera remboursé s'il a déjà été payé. À moins de J-30, aucun remboursement n'est possible — sauf si une personne inscrite sur la liste d'attente peut te remplacer, ou si tu trouves toi-même quelqu'un ; dans ce cas seul le solde (déjà payé) t'est remboursé, l'acompte restant acquis.",
  },
  {
    q: "Comment se déroule le paiement ?",
    a: "Un acompte de 30 % en ligne (CB ou Klarna en 3 fois sans frais) bloque ta réservation. Le solde de 70 % est à régler au plus tard à J-30 (un email de rappel est envoyé à J-45) — également payable en 3 fois via Klarna au moment du règlement du solde. Tu peux aussi choisir de payer l'intégralité dès la réservation. Autre option : passe à la boutique de Montélimar pour régler ton séjour en espèces — on t'inscrit sur place.",
  },
  {
    q: "Les repas sont-ils inclus ?",
    a: "Oui — buffet d'accueil, dîners, déjeuners et pauses thé, tisane, café et collations à volonté tout au long du séjour. Cuisine veggie simple et savoureuse.\n\nEn formule Immersion (avec hébergement), les petits-déjeuners sont inclus.\nEn formule Day (sans hébergement), les petits-déjeuners ne sont pas inclus — tu les prends chez toi avant de nous rejoindre.",
  },
];

const MEDITATION_HERO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_spirituel-wellness/artifacts/nyif3bhr_personne%20qui%20me%CC%81dite.jpg";
const GROUP_CIRCLE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_spirituel-wellness/artifacts/q7e7bbo6_personnes%20qui%20me%CC%81ditent.jpg";
const NATURE_VIEW = "https://customer-assets-lxgj4vgw.emergentagent.net/job_spirituel-wellness/artifacts/ucvcdgvx_Mas%20d%27archas%20dehors.jpg";

export default function RetreatsPage() {
  const [retreats, setRetreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/retreats`)
      .then((r) => setRetreats(r.data.retreats || []))
      .catch(() => setRetreats([]))
      .finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Séjours & Retraites spirituelles Bilan 2 Vie",
    description: "Séjours et retraites spirituels dans la Drôme provençale, animés par Guillaume Bilan 2 Vie. Méditation, soins énergétiques, ateliers, en pleine nature. Départs depuis Montélimar, à 1h de Valence, 2h de Lyon.",
    url: `${SITE_URL}/retraites`,
    inLanguage: "fr-FR",
    isPartOf: {
      "@type": "WebSite",
      name: "Bilan 2 Vie",
      url: SITE_URL,
    },
    about: {
      "@type": "Thing",
      name: "Retraite spirituelle et bien-être",
    },
    provider: {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#localbusiness`,
      name: "Bilan 2 Vie",
      url: SITE_URL,
      areaServed: [
        { "@type": "AdministrativeArea", name: "Drôme" },
        { "@type": "AdministrativeArea", name: "Auvergne-Rhône-Alpes" },
        { "@type": "AdministrativeArea", name: "Provence-Alpes-Côte d'Azur" },
        { "@type": "Country", name: "France" },
      ],
    },
  };

  // Breadcrumb — permet l'affichage fil d'Ariane dans les SERP Google.
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Séjours & Retraites", item: `${SITE_URL}/retraites` },
    ],
  };

  // FAQPage — active les rich snippets FAQ dans Google.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // ItemList — liste des séjours à venir (aide au crawl + peut apparaître en carrousel).
  const itemListLd = retreats.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: retreats.map((r, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}/retraites/${r.slug}`,
      name: r.title,
    })),
  } : null;

  // AggregateRating + Reviews — active les ⭐ dans les SERP Google.
  // Note moyenne "5" = ces avis 100% positifs, ratingCount = nb témoignages.
  const reviewsLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Séjours & Retraites spirituelles Bilan 2 Vie",
    description: "Séjours spirituels en Drôme provençale — méditation, soins énergétiques, ateliers de reconnexion.",
    brand: { "@type": "Organization", name: "Bilan 2 Vie" },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5",
      reviewCount: TESTIMONIALS.length,
      bestRating: "5",
      worstRating: "5",
    },
    review: TESTIMONIALS.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody: t.text,
    })),
  };

  return (
    <div className="min-h-screen bg-[#faf6ef] text-[#2a2418]">
      <Helmet>
        <title>Séjours & Retraites spirituels en France — Drôme, Provence | Bilan 2 Vie</title>
        <meta
          name="description"
          content="Séjours et retraites spirituels dans la Drôme provençale, animés par Guillaume (Bilan 2 Vie). Méditation, soins énergétiques, ateliers de reconnexion, en pleine nature. À 1h de Valence, 2h de Lyon, 2h30 de Marseille. Paiement en 3-4 fois via Klarna."
        />
        <meta
          name="keywords"
          content="retraite spirituelle, séjour spirituel, retraite bien-être Drôme, retraite méditation Provence, séjour ressourcement France, retraite yoga méditation, retraite énergétique, retraite spirituelle sud France, Mas d'Archas, Bilan 2 Vie"
        />
        <meta name="geo.region" content="FR-26" />
        <meta name="geo.placename" content="Montélimar, Drôme, Provence" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={`${SITE_URL}/retraites`} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bilan 2 Vie" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:title" content="Séjours & Retraites spirituels — Drôme, Provence | Bilan 2 Vie" />
        <meta property="og:description" content="Un séjour spirituel pour te réaligner en profondeur. Méditations, soins et ateliers en pleine nature — Drôme provençale." />
        <meta property="og:url" content={`${SITE_URL}/retraites`} />
        <meta property="og:image" content={MEDITATION_HERO} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Séjours & Retraites spirituels — Drôme, Provence" />
        <meta name="twitter:description" content="Un séjour spirituel pour te réaligner. Méditations, soins et ateliers en pleine nature." />
        <meta name="twitter:image" content={MEDITATION_HERO} />
        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(reviewsLd)}</script>
        {itemListLd && <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>}
      </Helmet>

      {/* Hero cinématographique */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden">
        <img src={MEDITATION_HERO} alt="Méditation en pleine nature" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-[#faf6ef]" />
        <div className="relative h-full flex flex-col items-center justify-end pb-20 px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <span className="inline-block text-xs tracking-[0.3em] uppercase text-white/80 mb-6" data-testid="retreats-eyebrow">Séjours & Retraites spirituels</span>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-light leading-[1.1] text-white mb-6" data-testid="retreats-h1">
              Respire.<br />Ralentis.<br />Reviens à toi.
            </h1>
            <p className="text-white/85 text-base sm:text-lg italic font-light max-w-xl mx-auto leading-relaxed">
              Un temps de pause en pleine nature pour te reconnecter à toi, à ton intuition et à tes énergies.
            </p>
          </motion.div>
          <a href="#prochaines-dates" className="mt-12 text-white/70 text-xs tracking-widest uppercase hover:text-white transition flex flex-col items-center gap-2 group">
            <span>Voir les prochaines dates</span>
            <span className="animate-bounce">↓</span>
          </a>
        </div>
      </section>

      {/* Accroche narrative */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-6">L'invitation</div>
        <p className="font-serif text-2xl sm:text-3xl leading-relaxed text-[#2a2418] font-light">
          Tu as la tête pleine, tu te sens fatigué·e énergétiquement,<br className="hidden sm:block" /> ou tu traverses une période de transition&nbsp;?
        </p>
        <p className="mt-8 text-lg text-[#5d5240] leading-relaxed max-w-2xl mx-auto">
          Cette retraite spirituelle est pensée pour t'aider à couper avec le quotidien, reprendre ton souffle et entendre à nouveau ta propre boussole intérieure. Nos séjours se déroulent <strong className="text-[#2a2418]">en pleine nature dans la Drôme provençale, à 1h de Valence, 2h de Lyon et 2h30 de Marseille</strong> — un cadre idéal pour venir depuis toute la France. Ici, pas de dogme ni de mysticisme flou&nbsp;: une approche ancrée, bienveillante et respectueuse du rythme de chacun·e.
        </p>
        <div className="mt-10 flex flex-wrap gap-6 justify-center text-sm text-[#8b7d5e]">
          <span className="flex items-center gap-2"><BedDouble className="w-4 h-4" /> Avec ou sans hébergement</span>
          <span className="w-1 h-1 rounded-full bg-[#c8b98a] self-center" />
          <span className="flex items-center gap-2"><Utensils className="w-4 h-4" /> Repas veggies inclus</span>
        </div>
      </section>

      {/* Séparateur photo */}
      <section className="relative h-[50vh] min-h-[400px] w-full overflow-hidden">
        <img src={NATURE_VIEW} alt="Le lieu, cocon en pleine nature" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#faf6ef]/50 to-transparent" />
      </section>

      {/* Bénéfices */}
      <section className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">Ce que tu vas vivre</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#2a2418]">Un séjour pour te réaligner en profondeur</h2>
          <p className="mt-6 text-base text-[#5d5240] leading-relaxed max-w-2xl mx-auto">
            Que tu viennes de <strong className="text-[#2a2418]">Paris, Lyon, Marseille ou de la région</strong>, nos retraites spirituelles rassemblent une douzaine de personnes autour d'un même besoin&nbsp;: ralentir, se reconnecter, et repartir plus léger·e.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-10">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="w-14 h-14 rounded-full bg-[#e8dfc6] flex items-center justify-center mx-auto mb-5">
                <b.icon className="w-6 h-6 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg mb-2 text-[#2a2418]">{b.title}</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Rassurance paiement : plusieurs fois (Klarna) & espèces boutique */}
      <section className="bg-[#f5efdf] border-y border-[#e8dfc6]" data-testid="retreats-payment-reassurance">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">Facilités de paiement</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#2a2418]">Réserve sereinement, paie à ton rythme</h2>
            <p className="text-sm text-[#5d5240] mt-4 max-w-xl mx-auto">
              Un séjour se prépare — le paiement aussi. Choisis la formule qui te convient.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-white border border-[#e8dfc6] rounded-xl p-6 shadow-sm"
            >
              <div className="w-11 h-11 rounded-full bg-[#e8dfc6] flex items-center justify-center mb-4">
                <Sparkles className="w-5 h-5 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg text-[#2a2418] mb-2">Acompte 30 %</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">
                Bloque ta place avec seulement <strong className="text-[#2a2418]">30 %</strong> à la réservation. Le solde est à régler à J-30.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="bg-white border-2 border-[#F5D8A2] rounded-xl p-6 shadow-md relative"
            >
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-widest uppercase bg-[#F5D8A2] text-[#2a2418] px-3 py-1 rounded-full font-medium">
                Le + demandé
              </span>
              <div className="w-11 h-11 rounded-full bg-[#F5D8A2]/40 flex items-center justify-center mb-4">
                <CreditCard className="w-5 h-5 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg text-[#2a2418] mb-2">Paiement en plusieurs fois</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">
                Via <strong className="text-[#2a2418]">Klarna</strong>, réparti ton séjour en <strong>3 mensualités</strong> — sans frais, décision immédiate à l'étape de paiement. <strong>L'acompte ET le solde</strong> sont éligibles au paiement fractionné.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="bg-white border border-[#e8dfc6] rounded-xl p-6 shadow-sm"
            >
              <div className="w-11 h-11 rounded-full bg-[#e8dfc6] flex items-center justify-center mb-4">
                <Store className="w-5 h-5 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg text-[#2a2418] mb-2">Règlement en espèces à la boutique</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">
                Tu préfères régler en main propre&nbsp;? Passe à la boutique de Montélimar — <strong className="text-[#2a2418]">on t'inscrit sur place</strong> et on encaisse en espèces.
              </p>
            </motion.div>
          </div>
          <p className="text-center text-xs text-[#8b7d5e] mt-8 italic">
            Une question sur le paiement&nbsp;? Écris-nous à <a href="mailto:guillaume@bilan2vie.fr" className="underline hover:text-[#2a2418]">guillaume@bilan2vie.fr</a>
          </p>
        </div>
      </section>

      {/* Photo groupe + citation */}
      <section className="relative py-24 overflow-hidden">
        <img src={GROUP_CIRCLE} alt="Cercle de méditation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2a2418]/55" />
        <div className="relative max-w-3xl mx-auto px-6 text-center text-white">
          <div className="text-6xl font-serif text-[#F5D8A2] leading-none mb-4">"</div>
          <p className="font-serif text-2xl sm:text-3xl font-light italic leading-relaxed">
            Une parenthèse hors du temps, où l'on repart avec des outils concrets — et surtout, avec soi-même.
          </p>
        </div>
      </section>

      {/* Prochaines dates */}
      <section id="prochaines-dates" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">Réserver</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#2a2418]">Prochaines dates</h2>
        </div>

        {loading ? (
          <div className="text-[#8b7d5e] italic text-center">Chargement…</div>
        ) : retreats.length === 0 ? (
          <div className="bg-white border border-[#e8dfc6] rounded-lg p-10 text-center max-w-2xl mx-auto">
            <p className="text-[#8b7d5e] italic mb-5">
              Aucune retraite programmée pour le moment. Reviens bientôt, ou inscris-toi à la newsletter pour être prévenu·e des prochaines dates.
            </p>
            <button
              type="button"
              onClick={() => setNewsletterOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#2a2418] text-white rounded-full text-xs tracking-widest uppercase font-medium hover:bg-[#3d4d3f] transition-all"
              data-testid="retreats-newsletter-open-empty"
            >
              📬 M'inscrire à la newsletter
            </button>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-8" data-testid="retreats-grid">
              {retreats.map((r) => (
                <RetreatCard key={r.retreat_id} r={r} />
              ))}
            </div>
            <div className="text-center mt-10">
              <button
                type="button"
                onClick={() => setNewsletterOpen(true)}
                className="inline-flex items-center gap-2 text-sm text-[#8b7d5e] hover:text-[#2a2418] transition-colors underline underline-offset-4"
                data-testid="retreats-newsletter-open-list"
              >
                📬 Ces dates ne te conviennent pas ? Reçois les prochaines par email
              </button>
            </div>
          </>
        )}
      </section>

      {newsletterOpen && <NewsletterModal onClose={() => setNewsletterOpen(false)} />}

      {/* Retraites par région — bloc de maillage interne vers landings locales SEO.
          Reste discret visuellement, sert principalement le référencement. */}
      <section className="bg-white border-y border-[#e8dfc6]" data-testid="retreats-regions-block">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-3">Retraites par région</div>
          <h2 className="font-serif text-2xl sm:text-3xl font-light text-[#2a2418] mb-8">
            Nos retraites spirituelles à travers le sud
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/retraite-spirituelle-drome-provence"
              className="group block bg-[#faf6ea] border border-[#e8dfc6] rounded-xl p-5 hover:border-[#c8b98a] hover:shadow-md transition-all text-left"
              data-testid="link-region-drome"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#8b7d5e] mb-2">Ancrage local</div>
              <div className="font-serif text-lg text-[#2a2418] mb-1 group-hover:underline">Retraite spirituelle en Drôme provençale</div>
              <div className="text-xs text-[#5d5240]">Lavande, oliviers et silence — à 1h de Valence</div>
            </Link>
            <Link
              to="/retraite-spirituelle-sud-france"
              className="group block bg-[#faf6ea] border border-[#e8dfc6] rounded-xl p-5 hover:border-[#c8b98a] hover:shadow-md transition-all text-left"
              data-testid="link-region-sud"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#8b7d5e] mb-2">Grand sud</div>
              <div className="font-serif text-lg text-[#2a2418] mb-1 group-hover:underline">Retraite spirituelle dans le sud de la France</div>
              <div className="text-xs text-[#5d5240]">Accessible depuis Lyon, Marseille, Montpellier, Genève</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Témoignages — carrousel infini défilant lentement de droite à gauche.
          Palette sauge profond pour évoquer nature/wellness spa. Pause au survol,
          scroll manuel possible. Ancre `#temoignages` utilisée par les landings. */}
      <section id="temoignages" className="relative overflow-hidden text-white" data-testid="retreats-testimonials" style={{ backgroundColor: "#3d4d3f" }}>
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <div className="text-[#dcc98e] text-xs tracking-[0.3em] uppercase mb-4">Ils l'ont vécu</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-light">Les retours des précédentes participant·es</h2>
            <div className="flex items-center justify-center gap-2 mt-6">
              {[0,1,2,3,4].map((i) => (
                <Star key={i} className="w-5 h-5 fill-[#dcc98e] text-[#dcc98e]" />
              ))}
              <span className="ml-2 text-sm text-white/70">— {TESTIMONIALS.length} retours de participant·es</span>
            </div>
            <p className="text-xs text-white/50 italic mt-3">Survole pour mettre en pause, ou fais défiler manuellement</p>
          </div>
        </div>

        {/* Marquee container — full-bleed pour un effet immersif */}
        <div
          className="testimonials-marquee relative w-full overflow-x-auto overflow-y-hidden pb-16 group"
          data-testid="testimonials-marquee"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {/* Fondu latéral gauche / droite pour effet infini (sauge) */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 z-10" style={{ background: "linear-gradient(to right, #3d4d3f, transparent)" }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 z-10" style={{ background: "linear-gradient(to left, #3d4d3f, transparent)" }} />
          <div className="testimonials-track flex gap-6 px-6" style={{ width: "max-content" }}>
            {/* Liste dupliquée pour un loop infini fluide sans saccade */}
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <blockquote
                key={`${t.name}-${i}`}
                className="testimonials-card shrink-0 w-[340px] sm:w-[380px] rounded-xl p-6 transition-colors"
                style={{
                  backgroundColor: "rgba(255,255,255,0.06)",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: "rgba(220,201,142,0.18)",
                }}
                data-testid={i < TESTIMONIALS.length ? `testimonial-${t.name.toLowerCase()}` : undefined}
              >
                <Quote className="w-6 h-6 mb-3" style={{ color: "rgba(220,201,142,0.5)" }} />
                <p className="text-sm text-white/90 leading-relaxed italic mb-5 min-h-[140px]">
                  « {t.text} »
                </p>
                <footer className="flex items-center gap-2 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-serif" style={{ backgroundColor: "rgba(220,201,142,0.22)", color: "#dcc98e" }}>
                    {t.name.charAt(0)}
                  </div>
                  <cite className="not-italic text-sm font-medium text-white">{t.name}</cite>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-white/50 italic pb-16">
          Témoignages authentiques recueillis par email suite aux éditions passées, publiés avec l'accord des participant·es.
        </p>

        {/* Styles marquee — animation CSS infinie, pause au survol, cache la scrollbar */}
        <style>{`
          .testimonials-marquee::-webkit-scrollbar { display: none; }
          @keyframes testimonials-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .testimonials-track {
            animation: testimonials-scroll 90s linear infinite;
          }
          .testimonials-marquee:hover .testimonials-track,
          .testimonials-card:hover ~ .testimonials-track {
            animation-play-state: paused;
          }
          .testimonials-card:hover {
            border-color: rgba(220,201,142,0.5) !important;
          }
          @media (prefers-reduced-motion: reduce) {
            .testimonials-track { animation: none; }
          }
        `}</style>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">Réponses</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#2a2418]">Questions fréquentes</h2>
        </div>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <div key={i} className="bg-white border border-[#e8dfc6] rounded-lg overflow-hidden">
              <button
                data-testid={`retreats-faq-${i}`}
                className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 hover:bg-[#faf6ef]"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="font-serif text-[#2a2418]">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#8b7d5e] transition-transform shrink-0 ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && (
                <div className="px-6 pb-6 text-[#5d5240] leading-relaxed text-sm">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Aide : appeler en cas de difficulté pour réserver un séjour en ligne */}
      <p className="pb-20 px-6 text-center text-xs text-[#8b7d5e] max-w-md mx-auto leading-relaxed" data-testid="retreats-help-call">
        Une difficulté pour réserver ton séjour en ligne ?{' '}
        <a href="tel:+33768489056" className="text-[#3d4d3f] hover:underline font-medium whitespace-nowrap">
          Appelle-moi au 07&nbsp;68&nbsp;48&nbsp;90&nbsp;56
        </a>{' '}et on organise ta réservation ensemble.
      </p>
    </div>
  );
}

function RetreatCard({ r }) {
  const dates = formatDatesRange(r.start_date, r.end_date);
  // Badge "Plus que X places" — n'apparaît que si le compteur est renseigné et
  // <= 30 % de la capacité totale, pour ne pas décrédibiliser un séjour qui vient
  // d'ouvrir (montrer "Plus que 12 sur 12" ferait bizarre). Toujours visible
  // quand ≤ 3 places pour maximiser l'urgence.
  const spots = typeof r.spots_remaining === "number" ? r.spots_remaining : null;
  const capacity = typeof r.total_capacity === "number" ? r.total_capacity : 0;
  const showUrgency =
    !r.retreat_full &&
    spots !== null &&
    spots > 0 &&
    (spots <= 3 || (capacity > 0 && spots / capacity <= 0.3));
  const critical = spots !== null && spots <= 3;

  return (
    <Link
      to={`/retraites/${r.slug}`}
      data-testid={`retreat-card-${r.slug}`}
      className="group block bg-white border border-[#e8dfc6] rounded-xl overflow-hidden hover:border-[#c8b98a] hover:shadow-xl transition-all duration-500"
    >
      {r.cover_image && (
        <div className="aspect-[16/10] overflow-hidden bg-[#e8dfc6] relative">
          <img
            src={r.cover_image}
            alt={r.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          {showUrgency && (
            <span
              data-testid={`retreat-card-spots-${r.slug}`}
              className={`absolute top-4 left-4 text-[11px] tracking-wider uppercase font-medium px-3 py-1.5 rounded-full shadow-md backdrop-blur-sm ${
                critical
                  ? "bg-red-500/95 text-white animate-pulse"
                  : "bg-[#F5D8A2]/95 text-[#2a2418]"
              }`}
            >
              {spots === 1 ? "Dernière place !" : `Plus que ${spots} places`}
            </span>
          )}
        </div>
      )}
      <div className="p-7">
        <div className="flex items-center gap-3 text-xs text-[#8b7d5e] tracking-wide mb-3">
          <Calendar className="w-4 h-4" />{dates}
          {r.location_city && (<><span className="text-[#c8b98a]">•</span><MapPin className="w-4 h-4" />{r.location_city}</>)}
        </div>
        <h3 className="font-serif text-2xl mb-3 text-[#2a2418]">{r.title}</h3>
        {r.subtitle && <p className="text-[#5d5240] text-sm mb-5 leading-relaxed">{r.subtitle}</p>}
        <div className="flex items-center justify-between pt-4 border-t border-[#e8dfc6]">
          {r.retreat_full ? (
            <span className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-full">Complet · liste d'attente</span>
          ) : r.starting_price ? (
            <span className="text-[#5d5240] text-sm">À partir de <strong className="text-[#2a2418]">{Math.round(r.starting_price)}€</strong></span>
          ) : <span />}
          <span className="text-[#8b7d5e] text-sm inline-flex items-center gap-1 group-hover:gap-2 group-hover:text-[#2a2418] transition-all">
            Découvrir <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function formatDatesRange(start, end) {
  try {
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    const opts = { day: "2-digit", month: "short" };
    return `${s.toLocaleDateString("fr-FR", opts)} → ${e.toLocaleDateString("fr-FR", { ...opts, year: "numeric" })}`;
  } catch {
    return `${start} → ${end}`;
  }
}


/**
 * Modale d'inscription newsletter — utilisée sur la page pilier retraites
 * quand aucune date n'est programmée OU quand l'utilisateur veut être prévenu
 * de dates futures. Envoie l'email vers `/api/newsletter/subscribe` qui pousse
 * ensuite le contact dans la liste Brevo via `nl_svc.add_contact`.
 */
function NewsletterModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [rgpd, setRgpd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Merci de renseigner un email valide.");
      return;
    }
    if (!rgpd) {
      toast.error("Merci d'accepter la politique de confidentialité pour recevoir la newsletter.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        source: "retreats",
      });
      setDone(true);
      toast.success("Inscription confirmée · à bientôt !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-modal-title"
      data-testid="newsletter-modal"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#faf6ea] w-full max-w-md rounded-xl shadow-2xl border border-[#e8dfc6] overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-[#8b7d5e] hover:text-[#2a2418] text-xl leading-none"
          aria-label="Fermer"
          data-testid="newsletter-close"
        >×</button>

        {!done ? (
          <form onSubmit={submit} className="p-8">
            <div className="text-center mb-6">
              <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-3">Ne rate rien</div>
              <h3 id="newsletter-modal-title" className="font-serif text-2xl text-[#2a2418] mb-3">
                Reçois les prochaines dates par email
              </h3>
              <p className="text-sm text-[#5d5240]">
                Un email court quand un nouveau séjour spirituel est annoncé. Pas de spam, désinscription en un clic.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="nl-first" className="block text-xs text-[#8b7d5e] uppercase tracking-wider mb-1.5">
                  Prénom (facultatif)
                </label>
                <input
                  id="nl-first"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full bg-white border border-[#e8dfc6] rounded px-4 py-2.5 text-sm text-[#2a2418] focus:outline-none focus:border-[#8b7d5e]"
                  data-testid="newsletter-first-name"
                  placeholder="Aurélie"
                />
              </div>
              <div>
                <label htmlFor="nl-email" className="block text-xs text-[#8b7d5e] uppercase tracking-wider mb-1.5">
                  Email *
                </label>
                <input
                  id="nl-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-white border border-[#e8dfc6] rounded px-4 py-2.5 text-sm text-[#2a2418] focus:outline-none focus:border-[#8b7d5e]"
                  data-testid="newsletter-email"
                  placeholder="ton@email.fr"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer text-xs text-[#5d5240] leading-relaxed">
                <input
                  type="checkbox"
                  checked={rgpd}
                  onChange={(e) => setRgpd(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#3d4d3f] shrink-0 cursor-pointer"
                  data-testid="newsletter-rgpd"
                />
                <span>
                  J'accepte de recevoir la newsletter de Bilan 2 Vie et j'ai lu la{" "}
                  <a
                    href="/politique-confidentialite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[#2a2418]"
                  >
                    politique de confidentialité
                  </a>
                  . Je peux me désinscrire à tout moment.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !rgpd || !email.trim()}
              className="w-full mt-6 px-6 py-3 bg-[#2a2418] text-white rounded-full text-xs tracking-widest uppercase font-medium hover:bg-[#3d4d3f] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="newsletter-submit"
            >
              {submitting ? "Inscription…" : "M'inscrire à la newsletter"}
            </button>
          </form>
        ) : (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">✨</div>
            <h3 className="font-serif text-xl text-[#2a2418] mb-3">C'est fait !</h3>
            <p className="text-sm text-[#5d5240] mb-6">
              Tu es maintenant inscrit·e. On te préviendra dès qu'un nouveau séjour est annoncé.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#3d4d3f] text-white rounded-full text-xs tracking-widest uppercase font-medium hover:bg-[#2a2418] transition-all"
              data-testid="newsletter-close-done"
            >
              Parfait, merci
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
